import { SelectionModel } from '@angular/cdk/collections';
import { computed, DestroyRef, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Row } from '../components/model-driven-cell/interface';
import { GalaxySelectionComponent } from '../components/selection/selection.component';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

interface SelectionEntry<T> {
  select: GalaxySelectionComponent<T>;
  valueSub: Subscription | null;
}

export function getRowId(row: unknown): unknown {
  if (row && typeof row === 'object' && 'id' in row) {
    return row.id;
  }
  return undefined;
}

export type SelectionMode = 'single' | 'multiple';

export interface GalaxyTableSelectionCustomization {
  rowsSelectionLimit?: number;
}
export const GalaxyTableSelectionCustomizationToken = new InjectionToken<GalaxyTableSelectionCustomization>(
  'optional dependencies for the selection customization',
);

@Injectable()
export class GalaxyTableSelectionService<T = Row> {
  entries: SelectionEntry<T>[] = [];

  private selectionMode$$ = new BehaviorSubject<SelectionMode>('multiple');

  multiSelection = new SelectionModel<T>(true, [], true, GalaxyTableSelectionService.compareRows);
  singleSelection = new SelectionModel<T>(false, [], true, GalaxyTableSelectionService.compareRows);

  private destroyRef = inject(DestroyRef);
  private readonly selectionCustomization: GalaxyTableSelectionCustomization | null = inject(
    GalaxyTableSelectionCustomizationToken,
    { optional: true },
  );
  rowsSelectionLimit = this.selectionCustomization?.rowsSelectionLimit || 500;

  readonly pageRowCount = signal(0);
  readonly totalRecords = signal(0);
  readonly selectAllMode = signal(false);

  private selectionModeSig = toSignal(this.selectionMode$$.asObservable());
  selectionModel = computed(() => {
    const mode = this.selectionModeSig();
    if (mode === 'single') {
      return this.singleSelection;
    }
    return this.multiSelection;
  });

  // We only want to emit what is selected, not the diffs
  selection$ = toObservable(this.selectionModel).pipe(
    switchMap((selectionModel) => {
      return selectionModel.changed.pipe(map(() => selectionModel.selected));
    }),
  );

  readonly selectedCount = toSignal(this.selection$.pipe(map((selection) => selection.length)), {
    initialValue: 0,
  });

  allSelected$ = toObservable(this.selectionModel).pipe(
    switchMap((selectionModel) => {
      return selectionModel.changed.pipe(
        map(() =>
          this.entries.every((entry) => (entry.select.row ? selectionModel.isSelected(entry.select.row) : false)),
        ),
      );
    }),
  );

  hasSelection$ = toObservable(this.selectionModel).pipe(
    switchMap((selectionModel) => {
      return selectionModel.changed.pipe(map(() => selectionModel.hasValue()));
    }),
  );

  static compareRows(row1: unknown, row2: unknown): boolean {
    const id1 = getRowId(row1);
    const id2 = getRowId(row2);
    if (id1 && id2) {
      return id1 === id2;
    }
    return row1 === row2;
  }

  private removeEntry(select: GalaxySelectionComponent<T>): SelectionEntry<T> | undefined {
    const index = this.entries.findIndex((entry) => entry.select === select);
    if (index === -1) {
      return undefined;
    }
    return this.entries.splice(index, 1)[0];
  }

  /** The "all control" selection has been updated */
  mainChanged(checked: boolean): void {
    if (!checked) {
      this.multiSelection.clear();
      return;
    }
    this.entries.forEach((entry) => {
      if (
        entry.select.row &&
        !this.isSelected(entry.select.row) &&
        this.multiSelection?.selected?.length < this.rowsSelectionLimit
      ) {
        this.multiSelection.select(entry.select.row);
      }
    });
  }

  /**
   * Register a component and its row for selection events
   * @param selectComp - The select component to register and listen to
   */
  registerRow(selectComp: GalaxySelectionComponent<T>): void {
    const entry: SelectionEntry<T> = {
      select: selectComp,
      valueSub: null,
    };

    // register the lord of checkboxes
    if (!selectComp.row) {
      entry.valueSub = selectComp.selected$$
        .pipe(
          filter((a) => a !== null),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (checked) => this.mainChanged(!!checked),
        });
    } else {
      entry.valueSub = selectComp.selected$$
        .pipe(
          filter((a) => a !== null),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            if (entry.select.row) this.onSelection(entry.select.row);
          },
        });
      this.entries.push(entry);
      this.pageRowCount.set(this.entries.length);
    }
  }

  /**
   * Unregister a selection component and its row
   * @param selectComp - The selection component to unregister
   */
  unregisterRow(selectComp: GalaxySelectionComponent<T>): void {
    const entry = this.removeEntry(selectComp);
    if (!entry) {
      return;
    }
    entry.valueSub?.unsubscribe();
    this.pageRowCount.set(this.entries.length);
  }

  isSelected(row: T): boolean {
    if (this.selectionMode$$.value === 'single') {
      return this.singleSelection.isSelected(row);
    }
    return this.multiSelection.isSelected(row);
  }

  onSelection(row: T): void {
    if (this.selectionMode$$.value === 'single') {
      this.singleSelection.toggle(row);
      return;
    }
    this.multiSelection.toggle(row);
  }

  clear() {
    this.singleSelection.clear();
    this.multiSelection.clear();
  }

  setSelectionMode(mode: SelectionMode) {
    this.selectionMode$$.next(mode);
  }

  setSelected(row: T) {
    if (this.selectionMode$$.value === 'single') {
      this.singleSelection.select(row);
      return;
    }
    this.multiSelection.select(row);
  }

  hasReachedSelectionLimit(row: T): boolean {
    if (this.isSelected(row)) {
      return false;
    }
    const selectedAmount = this.selectionModel().selected.length;
    return selectedAmount >= this.rowsSelectionLimit;
  }
}
